import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Newspaper, TrendingUp, Sparkles, RefreshCw, Instagram, Youtube } from 'lucide-react';
import { Outlet } from 'react-router-dom';
import BlogNewsCard from '../components/BlogNewsCard';
import AdSpace from '../components/AdSpace';
import NewsModal from '../components/NewsModal';
import { getNews, type NewsArticle } from '../services/newsService';
import { getPersonalizedAds, type Advertisement } from '../services/adService';
import { useTheme } from '../contexts/ThemeContext';

const FiquePorDentro: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [newsArticles, setNewsArticles] = useState<NewsArticle[]>([]);
  const [advertisements, setAdvertisements] = useState<Advertisement[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState<NewsArticle | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<string>('');

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    setLoading(true);
    try {
      const [news, ads] = await Promise.all([
        getNews(),
        getPersonalizedAds(3)
      ]);
      setNewsArticles(news);
      setAdvertisements(ads);
      
      // Atualiza timestamp
      const now = new Date();
      setLastUpdate(now.toLocaleTimeString('pt-BR', { 
        hour: '2-digit', 
        minute: '2-digit' 
      }));
    } catch (error) {
      console.error('Erro ao carregar conteúdo:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleArticleClick = (article: NewsArticle) => {
    setSelectedArticle(article);
    setIsModalOpen(true);
  };

  const handleRefresh = () => {
    loadContent();
  };

  const socialLinks = [
    {
      name: 'Instagram',
      description: 'Conteúdos diários e bastidores do Grana em Dia.',
      href: 'https://www.instagram.com/granaemdia.app/?igshid=YmMyMTA2M2Y=',
      icon: Instagram,
      gradient: isDark
        ? 'from-pink-500/80 via-amber-400/80 to-rose-500/80'
        : 'from-pink-500 via-amber-400 to-rose-500',
    },
    {
      name: 'YouTube',
      description: 'Séries completas sobre educação financeira.',
      href: 'https://www.youtube.com/watch?v=Sl8EG962qN0&list=PL4Dxp36aqd9npQjCWksDMLV2dBJxYnjH9',
      icon: Youtube,
      gradient: isDark
        ? 'from-red-500/80 via-orange-500/80 to-amber-400/80'
        : 'from-red-500 via-orange-500 to-amber-400',
    },
  ];

  return (
    <div className={`min-h-screen transition-colors ${isDark ? 'bg-slate-900' : 'bg-gradient-to-br from-slate-50 via-teal-50 to-cyan-50'}`}>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className={`p-3 rounded-xl shadow-lg transition-colors ${
              isDark 
                ? 'bg-gradient-to-br from-indigo-500 to-purple-600 shadow-indigo-500/30' 
                : 'bg-gradient-to-br from-teal-500 to-cyan-600 shadow-teal-500/30'
            }`}>
              <Newspaper className="text-white" size={28} />
            </div>
            <div className="flex-1">
              <h1 className={`text-3xl font-bold transition-colors ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Fique por Dentro
              </h1>
              <p className={`transition-colors ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                Notícias, dicas e tendências do mundo financeiro
              </p>
              {lastUpdate && (
                <p className={`text-xs mt-1 transition-colors ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                  📰 Atualizado às {lastUpdate} • Renovação diária automática
                </p>
              )}
            </div>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className={`p-3 rounded-xl transition-colors disabled:opacity-50 ${
                isDark 
                  ? 'bg-slate-800 hover:bg-slate-700' 
                  : 'bg-white hover:bg-teal-50 border border-teal-200 shadow-sm'
              }`}
              title="Atualizar notícias manualmente"
            >
              <RefreshCw className={`${loading ? 'animate-spin' : ''} transition-colors ${
                isDark ? 'text-indigo-400' : 'text-teal-500'
              }`} size={24} />
            </button>
          </div>
        </motion.div>

        {/* Destaque Principal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mb-8"
        >
          <div className={`relative rounded-2xl overflow-hidden border shadow-2xl transition-colors ${
            isDark 
              ? 'bg-gradient-to-br from-indigo-900/50 via-purple-900/30 to-slate-900 border-indigo-500/30'
              : 'bg-gradient-to-br from-white via-teal-50 to-cyan-100 border-teal-200'
          }`}>
            <div className={`absolute inset-0 ${
              isDark 
                ? 'bg-[radial-gradient(circle_at_50%_0%,rgba(99,102,241,0.15),rgba(0,0,0,0))]'
                : 'bg-[radial-gradient(circle_at_50%_0%,rgba(45,212,191,0.15),rgba(255,255,255,0))]'
            }`} />
            <div className="relative p-8">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="text-yellow-400" size={20} />
                <span className="text-sm font-bold uppercase tracking-wider text-yellow-400">
                  Em Destaque
                </span>
              </div>
              <h2 className={`text-4xl font-black mb-4 max-w-3xl transition-colors ${
                isDark ? 'text-white' : 'text-slate-900'
              }`}>
                Transforme sua Relação com o Dinheiro
              </h2>
              <p className={`text-lg mb-6 max-w-2xl transition-colors ${
                isDark ? 'text-slate-300' : 'text-slate-700'
              }`}>
                Acompanhe as principais notícias do mercado financeiro, aprenda estratégias de investimento e descubra como alcançar sua independência financeira.
              </p>
              <div className="flex gap-4">
                <a
                  href="https://www.infomoney.com.br"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`px-6 py-3 font-bold rounded-lg transition-all ${
                    isDark
                      ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:shadow-lg hover:shadow-indigo-500/50'
                      : 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white hover:shadow-lg hover:shadow-teal-500/50'
                  }`}
                >
                  Ver Principais Notícias
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Grid de Conteúdo */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Coluna Principal - Notícias */}
          <div className="lg:col-span-2 space-y-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className={`transition-colors ${isDark ? 'text-indigo-400' : 'text-teal-500'}`} size={20} />
              <h3 className={`text-xl font-bold transition-colors ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Últimas Notícias
              </h3>
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className={`animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 transition-colors ${
                  isDark ? 'border-indigo-500' : 'border-teal-500'
                }`}></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {newsArticles.map((article, index) => (
                  <div key={article.id} onClick={() => handleArticleClick(article)} className="cursor-pointer">
                    <BlogNewsCard {...article} index={index} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Coluna Lateral - Publicidade */}
          <div className="space-y-6">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="text-yellow-400" size={20} />
              <h3 className={`text-xl font-bold transition-colors ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Destaques
              </h3>
            </div>

            {/* Anúncios Premium */}
            {advertisements.map((ad, index) => (
              <AdSpace
                key={ad.id}
                id={ad.id}
                title={ad.title}
                description={ad.description}
                imageUrl={ad.imageUrl}
                ctaText={ad.ctaText}
                ctaUrl={ad.ctaUrl}
                size={ad.size}
                variant={ad.variant}
                index={index}
              />
            ))}
          </div>
        </div>

        {/* Redes sociais */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className={`rounded-xl p-8 border transition-colors ${
            isDark
              ? 'bg-gradient-to-br from-slate-900 via-slate-900/80 to-slate-900 border-indigo-500/40'
              : 'bg-gradient-to-br from-white via-teal-50 to-slate-50 border-emerald-200'
          }`}
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-center sm:text-left">
              <p className={`text-sm font-semibold uppercase tracking-[0.15em] ${
                isDark ? 'text-indigo-200/80' : 'text-teal-700'
              }`}>
                Comunidade Grana em Dia
              </p>
              <h3 className={`text-2xl font-bold transition-colors ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Acompanhe nossas redes sociais
              </h3>
              <p className={`mt-2 text-sm transition-colors ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                Fique por dentro das novidades e conteúdos exclusivos. Clique para abrir e seguir.
              </p>
            </div>
            <div className="grid w-full gap-3 sm:max-w-xl sm:grid-cols-2">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`group relative overflow-hidden rounded-xl border px-4 py-3 text-left transition-all ${
                      isDark
                        ? 'border-slate-700/60 bg-slate-800/60 hover:border-emerald-400/50'
                        : 'border-teal-100 bg-white hover:border-emerald-300'
                    }`}
                  >
                    <div
                      className={`absolute inset-0 opacity-70 blur-3xl transition-transform duration-500 group-hover:scale-110 bg-gradient-to-r ${social.gradient}`}
                    />
                    <div className="relative flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${isDark ? 'bg-slate-900/80' : 'bg-slate-50'}`}>
                        <Icon className={isDark ? 'text-white' : 'text-slate-800'} size={22} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-3">
                          <span className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            {social.name}
                          </span>
                          <span className={`text-xs font-semibold uppercase tracking-wide ${
                            isDark ? 'text-emerald-200' : 'text-emerald-700'
                          }`}>
                            Seguir
                          </span>
                        </div>
                        <p className={`mt-1 text-xs ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                          {social.description}
                        </p>
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        </motion.div>
      </div>
      
      {/* Modal de Leitura */}
      <NewsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        article={selectedArticle}
      />
      
      <Outlet />
    </div>
  );
};

export default FiquePorDentro;
